import { useCallback, useEffect, useState } from 'react';
import { History, Loader2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { emailNewsletterApi } from '../../../api/emailNewsletterApi';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

interface LogRow {
  id: number;
  subscriber_email?: string;
  template_name?: string;
  status?: string;
  sent_at?: string;
}

function formatDate(iso: string | undefined, localeTag: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(localeTag, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function DeliveryHistoryPage() {
  const { t, localeTag } = useUiLocale();
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Detail modal ── */
  const [detailLog, setDetailLog] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailNewsletterApi.listNewsletterLogs({ limit: 100 }, undefined);
      const raw = Array.isArray(res.data) ? res.data : [];
      setLogs(raw.map((r: any) => ({
        id: r.id,
        subscriber_email: r.subscriber_email ?? r.subscriber?.email ?? '—',
        template_name: r.template_name ?? r.template?.name ?? '—',
        status: r.status ?? '—',
        sent_at: r.sent_at ?? r.created_at,
      })));
    } catch {
      toast.error(t('deliveryHistory.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const openDetail = async (id: number) => {
    setDetailLog({ id });
    setLoadingDetail(true);
    try {
      const res = await emailNewsletterApi.getLog(id, undefined);
      setDetailLog(res.data);
    } catch {
      setDetailLog(null);
      toast.error(t('deliveryHistory.toastDetailFailed'));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await emailNewsletterApi.removeLog(deleteId, undefined);
      toast.success(t('deliveryHistory.toastDeleted'));
      setDeleteId(null);
      await loadLogs();
    } catch {
      toast.error(t('deliveryHistory.toastDeleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="hawk-admin-surface flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="hawk-glass-header hawk-glass-header--login shrink-0">
        <div className="px-1 py-2 sm:py-2.5">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            <span className="hawk-gradient-text">{t('deliveryHistory.title')}</span>
          </h1>
          <p className="hawk-page-subtitle mt-0.5 text-sm leading-tight">{t('deliveryHistory.subtitle')}</p>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <Card className="border-white/10 shadow-none">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
                <History className="h-8 w-8 text-slate-300" />
                <p className="text-sm">{t('deliveryHistory.empty')}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="hawk-table-head-row text-left">
                    <th className="px-4 py-3">{t('common.subscriber')}</th>
                    <th className="px-4 py-3">{t('common.template')}</th>
                    <th className="px-4 py-3">{t('common.status')}</th>
                    <th className="px-4 py-3">{t('deliveryHistory.colSentAt')}</th>
                    <th className="w-20 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr
                      key={log.id}
                      className={`cursor-pointer transition-colors hover:bg-slate-50 ${i > 0 ? 'border-t border-slate-200/70' : ''}`}
                      onClick={() => openDetail(log.id)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">{log.subscriber_email}</td>
                      <td className="px-4 py-3 text-slate-600">{log.template_name}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className={`hawk-outside-curve-pill font-medium capitalize text-xs ${
                            log.status === 'sent' || log.status === 'delivered'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                              : log.status === 'failed' || log.status === 'bounced'
                                ? 'border-red-200 bg-red-50 text-red-800'
                                : 'border-slate-200 bg-slate-50 text-slate-700'
                          }`}
                        >
                          {log.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(log.sent_at, localeTag)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDeleteId(log.id); }}
                          className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          title={t('deliveryHistory.deleteTitle')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail modal */}
      {detailLog != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <p className="font-semibold text-slate-800">{t('deliveryHistory.logTitle', { id: detailLog.id })}</p>
              <button type="button" onClick={() => setDetailLog(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              {loadingDetail ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <dl className="space-y-2 text-sm">
                  {Object.entries(detailLog).map(([k, v]) => (
                    <div key={k} className="flex gap-4">
                      <dt className="w-40 shrink-0 font-medium text-slate-500 capitalize">{k.replace(/_/g, ' ')}</dt>
                      <dd className="text-slate-800 break-all">{String(v ?? '—')}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <Button variant="outline" size="sm" onClick={() => setDetailLog(null)}>{t('common.close')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <AlertDialog open={deleteId != null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deliveryHistory.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('deliveryHistory.deleteDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
